import { bool, number, shape, func, object, oneOf, any } from 'prop-types'
import { useState, useEffect } from 'react'
import { Asset } from '@opallabs/cassini'
import Conditional from '../../../Conditional'
import ProgressBar from '../../../ProgressBar'
import { kuiperClassNames } from '../../../utils/kuiperClassNames'
import InfoOverlay from '../../InfoOverlay'
import MediaControls from '../../MediaControls'
import { styles } from './styles'

const hasAssetLoaded = (asset) => {
  if (!asset) return false

  if (asset.type === 'image') {
    return Boolean(asset.uuid)
  }

  // if media asset, we need to wait for the processing_percent to reach 100 or check if it's undefined

  return Boolean(
    asset.uuid &&
      !(asset.processing_percent && asset.processing_percent === 0) &&
      (asset.processing_percent >= 100 ||
        asset.processing_percent === undefined)
  )
}

const ImageBase = ({
  assetReference: asset,
  autoPlay,
  coverAsset,
  deletable,
  disableCoverAsset = false,
  downloadable,
  getDuration = () => null,
  hasAssetOptions,
  hasAssetOverlay,
  hasMediaControls = false,
  readOnly,
  imageFit,
  isHovered,
  isLimitedUser,
  infoOverlayStyles,
  onDelete,
  onEmbiggen,
  onRemove,
  overrideStyles = {},
  placeholder,
  preview,
  removeable,
  loaded,
  onLoad,
  onError,
  percent,
  thumbnail,
  transforms,
  updateCaption,
  updateCoverAsset,
  videoLoop,
}) => {
  const [mediaPlayerStatus, setMediaPlayerStatus] = useState(autoPlay)
  const [showMediaControls, setShowMediaControls] = useState(isHovered)
  const [displayAsset, setDisplayAsset] = useState(
    coverAsset && !disableCoverAsset && coverAsset.uuid ? coverAsset : asset
  )
  const assetHasLoaded = hasAssetLoaded(displayAsset)

  useEffect(() => {
    if (mediaPlayerStatus) {
      setDisplayAsset(asset)
    } else {
      setDisplayAsset(coverAsset && !disableCoverAsset ? coverAsset : asset)
    }
  }, [mediaPlayerStatus, asset, coverAsset, disableCoverAsset])

  return (
    <div
      className={kuiperClassNames('asset-base')}
      css={[styles.assetBase, overrideStyles?.assetBase]}
      onMouseEnter={() => setShowMediaControls(true)}
      onMouseLeave={() => setShowMediaControls(false)}
    >
      <div
        className={kuiperClassNames('asset')}
        css={[
          styles.asset({
            hasAsset: Boolean(asset),
            placeholder,
            loaded,
          }),
          overrideStyles?.asset,
        ]}
      >
        {displayAsset && assetHasLoaded && (
          <Asset
            autoPlay={autoPlay}
            assetReference={displayAsset}
            imageFit={imageFit}
            preview={Boolean(preview || thumbnail)}
            transforms={transforms}
            onLoad={onLoad}
            onError={onError}
            videoLoop={videoLoop}
          />
        )}
        <Conditional condition={asset && !assetHasLoaded}>
          <div css={styles.progressWrapper}>
            <ProgressBar
              fillColor={styles.fillColor}
              percent={percent}
              style={styles.progressBar}
            />
            <span css={styles.progressText}>Uploading...</span>
          </div>
        </Conditional>
      </div>

      <Conditional
        condition={
          assetHasLoaded && asset && asset.uuid && hasAssetOverlay && !readOnly
        }
      >
        <InfoOverlay
          assetReference={asset}
          coverAsset={coverAsset}
          deletable={deletable}
          downloadable={downloadable}
          hasAssetOptions={hasAssetOptions}
          hasAssetOverlay={hasAssetOverlay}
          infoOverlayStyles={infoOverlayStyles}
          isLimitedUser={isLimitedUser}
          onDelete={onDelete}
          onEmbiggen={onEmbiggen}
          onRemove={onRemove}
          removeable={removeable}
          updateCaption={updateCaption}
          updateCoverAsset={updateCoverAsset}
        />
      </Conditional>
      <Conditional
        condition={assetHasLoaded && asset && asset.uuid && hasMediaControls}
      >
        <MediaControls
          getDuration={getDuration}
          parentHovered={showMediaControls || isHovered}
          onPlayMedia={(isPlaying) => setMediaPlayerStatus(isPlaying)}
        />
      </Conditional>
    </div>
  )
}

ImageBase.propTypes = {
  assetReference: object,
  autoPlay: bool,
  coverAsset: object,
  deletable: bool,
  disableCoverAsset: bool,
  downloadable: bool,
  getDuration: func,
  hasAssetOptions: bool,
  hasAssetOverlay: bool,
  hasMediaControls: bool,
  imageFit: oneOf(['fill', 'contain', 'cover']),
  infoOverlayStyles: any,
  isHovered: bool,
  isLimitedUser: bool,
  loaded: bool,
  onDelete: func,
  onEmbiggen: func,
  onError: func,
  onLoad: func,
  onRemove: func,
  overrideStyles: shape({
    asset: any,
    assetBase: any,
  }),
  readOnly: bool,
  percent: number,
  placeholder: bool,
  preview: bool,
  removeable: bool,
  thumbnail: bool,
  transforms: object,
  updateCaption: func,
  updateCoverAsset: func,
  videoLoop: bool,
}

export default ImageBase
